/**
 * SoleadLab 2.0 — Copilot Panel (E2.3)
 *
 * Floating overlay for grounded copilot Q&A.
 * Users ask bounded site/asset questions and inspect grounding references.
 *
 * Scope control: toggle between "Entire Site" and a specific asset node.
 * When an asset is selected, asset_id is passed to the query so the backend
 * focuses context on that asset.
 *
 * Contract §10.4: The frontend displays answer_text with confidence indicator,
 * shows grounding references as clickable links to canonical data, displays
 * quality warnings prominently, shows uncertainty statements, and DOES NOT
 * recompute confidence or grounding.
 */

import { useState, useRef, useEffect } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useAssetNodeStore } from '@/stores/assetNodeStore';
import {
  submitCopilotQuery,
  type CopilotQueryResult,
  type GroundingReference,
  type UncertaintyStatement,
} from '@/services/copilotApi';
import '@/styles/copilot.css';

interface CopilotPanelProps {
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'refusal';
  text: string;
  timestamp: string;
  scopeLabel?: string;
  // Only for assistant messages:
  confidence?: number;
  grounding?: CopilotQueryResult['grounding'];
  qualityWarning?: string | null;
}

export default function CopilotPanel({ onClose }: CopilotPanelProps) {
  const siteId = useProjectStore((s) => s.meta.siteId);
  const siteName = useProjectStore((s) => s.meta.siteName);

  // Asset nodes for scope selector
  const assetItems = useAssetNodeStore((s) => s.items);
  const assetLoading = useAssetNodeStore((s) => s.loading);
  const setSiteIdForAssets = useAssetNodeStore((s) => s.setSiteId);
  const fetchAssetItems = useAssetNodeStore((s) => s.fetchItems);

  // Scope state: null = entire site, string = specific asset_id
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch asset nodes when siteId changes
  useEffect(() => {
    if (siteId) {
      setSiteIdForAssets(siteId);
      fetchAssetItems();
    }
  }, [siteId, setSiteIdForAssets, fetchAssetItems]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const selectedAsset = selectedAssetId
    ? assetItems.find((a) => a.id === selectedAssetId) ?? null
    : null;

  const scopeLabel = selectedAsset
    ? `${selectedAsset.displayName || selectedAsset.canonicalLabel} (${selectedAsset.assetType})`
    : 'Entire Site';

  const toggleRefExpand = (msgId: string) => {
    setExpandedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = input.trim();
    if (!query || !siteId || loading) return;

    setError(null);
    setInput('');

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: query,
      timestamp: new Date().toISOString(),
      scopeLabel,
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const result = await submitCopilotQuery(
        siteId,
        query,
        selectedAssetId ?? undefined,
      );

      if (result.answered) {
        const assistantMsg: ChatMessage = {
          id: result.answer_id || crypto.randomUUID(),
          role: 'assistant',
          text: result.answer_text || '',
          timestamp: new Date().toISOString(),
          confidence: result.confidence_score,
          grounding: result.grounding,
          qualityWarning: result.quality_warning,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      } else {
        const refusalMsg: ChatMessage = {
          id: result.query_id || crypto.randomUUID(),
          role: 'refusal',
          text: result.refusal_reason || 'Unable to answer this query.',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, refusalMsg]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit query');
    } finally {
      setLoading(false);
    }
  };

  const confidenceLabel = (score: number): { text: string; className: string } => {
    if (score >= 0.70) return { text: 'High', className: 'copilot-confidence--high' };
    if (score >= 0.50) return { text: 'Moderate', className: 'copilot-confidence--moderate' };
    return { text: 'Low', className: 'copilot-confidence--low' };
  };

  const sourceTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      site_state: 'Site State',
      asset_state: 'Asset State',
      score: 'Score',
      finding: 'Finding',
      observation: 'Observation',
      quality_report: 'Quality Report',
      site_metadata: 'Site Info',
    };
    return labels[type] || type;
  };

  return (
    <div className="copilot-panel-overlay" onClick={onClose}>
      <div className="copilot-panel-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="copilot-panel-header">
          <div className="copilot-panel-header__left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="copilot-panel-title">
              Site Copilot
              {siteName && <span className="copilot-panel-site"> — {siteName}</span>}
            </span>
          </div>
          <button className="copilot-panel-close" onClick={onClose} title="Close">&times;</button>
        </div>

        {/* Scope selector */}
        <div className="copilot-scope">
          <span className="copilot-scope-label">Scope:</span>
          <button
            className="copilot-scope-btn"
            type="button"
            onClick={() => setScopeOpen((v) => !v)}
            disabled={!siteId}
          >
            <span className={`copilot-scope-dot ${selectedAssetId ? 'copilot-scope-dot--asset' : 'copilot-scope-dot--site'}`} />
            {scopeLabel}
            <span className="copilot-scope-chevron">{scopeOpen ? '▴' : '▾'}</span>
          </button>
          {scopeOpen && (
            <div className="copilot-scope-dropdown">
              <button
                className={`copilot-scope-option ${!selectedAssetId ? 'copilot-scope-option--active' : ''}`}
                type="button"
                onClick={() => { setSelectedAssetId(null); setScopeOpen(false); }}
              >
                <span className="copilot-scope-dot copilot-scope-dot--site" />
                Entire Site
              </button>
              {assetLoading && (
                <div className="copilot-scope-loading">Loading assets...</div>
              )}
              {assetItems.map((node) => (
                <button
                  key={node.id}
                  className={`copilot-scope-option ${selectedAssetId === node.id ? 'copilot-scope-option--active' : ''}`}
                  type="button"
                  onClick={() => { setSelectedAssetId(node.id); setScopeOpen(false); }}
                >
                  <span className="copilot-scope-dot copilot-scope-dot--asset" />
                  <span className="copilot-scope-type">{node.assetType}</span>
                  {node.displayName || node.canonicalLabel}
                </button>
              ))}
              {!assetLoading && assetItems.length === 0 && (
                <div className="copilot-scope-loading">No assets found</div>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="copilot-messages">
          {messages.length === 0 && (
            <div className="copilot-empty">
              <p>Ask questions about this site's health, assets, scores, and findings.</p>
              <p className="copilot-empty-hint">Select a scope above to focus on the entire site or a specific asset.</p>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`copilot-msg copilot-msg--${msg.role}`}>
              {/* User message */}
              {msg.role === 'user' && (
                <div className="copilot-msg__bubble copilot-msg__bubble--user">
                  {msg.scopeLabel && (
                    <span className="copilot-msg-scope">{msg.scopeLabel}</span>
                  )}
                  {msg.text}
                </div>
              )}

              {/* Refusal message */}
              {msg.role === 'refusal' && (
                <div className="copilot-msg__bubble copilot-msg__bubble--refusal">
                  <span className="copilot-refusal-icon">&#9888;</span>
                  {msg.text}
                </div>
              )}

              {/* Assistant answer */}
              {msg.role === 'assistant' && (
                <div className="copilot-msg__bubble copilot-msg__bubble--assistant">
                  {/* Quality warning banner */}
                  {msg.qualityWarning && (
                    <div className="copilot-quality-warning">
                      &#9888; {msg.qualityWarning}
                    </div>
                  )}

                  {/* Answer text */}
                  <div className="copilot-answer-text">
                    {msg.text.split('\n').map((line, i) => (
                      <p key={i}>{line || '\u00A0'}</p>
                    ))}
                  </div>

                  {/* Confidence badge */}
                  {msg.confidence !== undefined && (
                    <div className="copilot-confidence-row">
                      <span className={`copilot-confidence-badge ${confidenceLabel(msg.confidence).className}`}>
                        {confidenceLabel(msg.confidence).text} confidence ({(msg.confidence * 100).toFixed(0)}%)
                      </span>
                    </div>
                  )}

                  {/* Uncertainty statements */}
                  {msg.grounding && msg.grounding.uncertainty_statements.length > 0 && (
                    <div className="copilot-uncertainty">
                      {msg.grounding.uncertainty_statements.map((u: UncertaintyStatement, i: number) => (
                        <div key={i} className="copilot-uncertainty-item">
                          <span className="copilot-uncertainty-tag">{u.reason.replace(/_/g, ' ')}</span>
                          {u.statement}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Grounding references — expandable */}
                  {msg.grounding && msg.grounding.references.length > 0 && (
                    <div className="copilot-grounding">
                      <button
                        className="copilot-grounding-toggle"
                        onClick={() => toggleRefExpand(msg.id)}
                        type="button"
                      >
                        {expandedRefs.has(msg.id) ? '▾' : '▸'} Sources ({msg.grounding.references.length})
                      </button>
                      {expandedRefs.has(msg.id) && (
                        <div className="copilot-grounding-list">
                          {msg.grounding.references.map((ref: GroundingReference) => (
                            <div key={ref.ref_id} className="copilot-ref-item">
                              <span className="copilot-ref-type">{sourceTypeLabel(ref.source_type)}</span>
                              <span className="copilot-ref-claim">{ref.claim}</span>
                              <span className="copilot-ref-id" title={`${ref.source_id}${ref.field ? '.' + ref.field : ''}`}>
                                {ref.source_id.substring(0, 12)}...
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="copilot-msg copilot-msg--assistant">
              <div className="copilot-msg__bubble copilot-msg__bubble--loading">
                Thinking...
              </div>
            </div>
          )}

          {error && (
            <div className="copilot-error">{error}</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="copilot-input-row" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            className="copilot-input"
            type="text"
            placeholder={
              !siteId
                ? 'Open a project first'
                : selectedAssetId
                  ? `Ask about ${selectedAsset?.canonicalLabel || 'this asset'}...`
                  : 'Ask about this site...'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!siteId || loading}
          />
          <button
            className="copilot-send"
            type="submit"
            disabled={!input.trim() || !siteId || loading}
            title="Send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>

      </div>
    </div>
  );
}
